import React from 'react';
import { useTranslation } from '../../localization/useAppTranslation';
import type { TourProgress } from '../../types/preferences';
import type { CoachTourDefinition, TourVariant } from '../../tours/types';
import { ThemedSpinner } from '../ThemedSpinner';

interface GuidedToursPanelProps {
  tours: readonly CoachTourDefinition[];
  variant: TourVariant;
  progress: TourProgress[] | null;
  isLoading: boolean;
  loadError: string | null;
  completionError: string | null;
  onRetryLoad: () => Promise<TourProgress[] | null>;
  onStartTour: (tourId: string) => void;
}

export const GuidedToursPanel: React.FC<GuidedToursPanelProps> = ({
  tours,
  variant,
  progress,
  isLoading,
  loadError,
  completionError,
  onRetryLoad,
  onStartTour,
}) => {
  const { t } = useTranslation('tour');
  const availableTours = tours.filter((tour) => tour.variants.includes(variant));

  if (progress === null) {
    if (loadError) {
      return (
        <div className="guided-tours-state" role="alert">
          <p>{loadError}</p>
          <button
            type="button"
            className="guided-tours-retry-button"
            onClick={() => void onRetryLoad()}
          >
            {t('common:actions.retry')}
          </button>
        </div>
      );
    }

    return (
      <div
        className="guided-tours-state"
        role="status"
        aria-live="polite"
        aria-busy={isLoading}
      >
        <ThemedSpinner size="md" ariaLabel={t('loading')} />
      </div>
    );
  }

  return (
    <section
      className="sidebar-panel-scroll guided-tours-panel"
      aria-label={t('ariaLabel')}
    >
      <div className="guided-tours-list">
        {availableTours.map((tour) => {
          const completedTour = progress.find((item) => item.tour_id === tour.id);
          const isComplete = (completedTour?.completed_version || 0) >= tour.version;

          return (
            <article className="guided-tour-card" key={tour.id}>
              <div className="guided-tour-card-copy">
                <h3>{t(tour.titleKey)}</h3>
                <p>{t(tour.descriptionKey)}</p>
                <p className="guided-tour-status">
                  {isComplete ? t('status.completed') : t('status.notStarted')}
                </p>
              </div>
              <button
                type="button"
                className="guided-tour-start-button"
                onClick={() => onStartTour(tour.id)}
              >
                {isComplete ? t('actions.takeAgain') : t('actions.startTour')}
              </button>
            </article>
          );
        })}
      </div>
      {completionError && (
        <div className="guided-tours-completion-error" role="alert">
          {completionError}
        </div>
      )}
    </section>
  );
};